import React from 'react';

import { GlobalMoreMenu } from '@atlassian/global-more-menu';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';

import type { NavItemConfigInternal } from '../../common/types';
import { usePeekMenuId } from '../../controllers/peek-menu-id-providers';

import { CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID, MORE_MENU_TEST_ID } from './constants';
import { CustomizeSidebarMenuItem } from './customize-sidebar-menu-item';
import MoreMenuItem from './more-menu-item';

export interface MoreMenuProps {
	appLabel: string;
	intlMode?: 'async' | 'inherit';
	itemIds: string[];
	items: Record<string, NavItemConfigInternal>;
	onClickCustomizeSidebarMenuItem?: () => void;
	onClickMoreMenuTrigger?: () => void;
	openShortcutsInSameTab?: boolean;
	shortcutItemIds: string[];
	shortcutItems?: Record<string, GlobalShortcutsItem>;
}

type MenuItemComponentType = React.ComponentProps<typeof GlobalMoreMenu>['menuItemComponent'];

export default function MoreMenu({
	appLabel,
	intlMode,
	itemIds,
	items,
	onClickCustomizeSidebarMenuItem,
	onClickMoreMenuTrigger,
	openShortcutsInSameTab,
	shortcutItemIds,
	shortcutItems = {},
}: MoreMenuProps): React.ReactNode {
	const peekMenuId = usePeekMenuId();

	const menuItemComponent: MenuItemComponentType = React.useCallback(
		({ closeMenu, menuId }: React.ComponentProps<MenuItemComponentType>) => {
			return (
				<MoreMenuItem
					id={menuId}
					key={menuId}
					items={items}
					onClick={closeMenu}
					openShortcutsInSameTab={openShortcutsInSameTab}
					shortcutItems={shortcutItems}
				/>
			);
		},
		[items, shortcutItems, openShortcutsInSameTab],
	);

	const productItemsData = React.useMemo(() => {
		return itemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [itemIds]);

	const appItemsData = React.useMemo(() => {
		return shortcutItemIds.map((menuId) => ({
			menuId,
			visible: false,
		}));
	}, [shortcutItemIds]);

	const customizeSidebarMenuItem = React.useMemo(() => {
		return (
			<CustomizeSidebarMenuItem
				items={items}
				shortcuts={shortcutItems}
				testId={CUSTOMIZE_SIDEBAR_MENU_ITEM_TEST_ID}
				appLabel={appLabel}
				onClick={onClickCustomizeSidebarMenuItem}
			/>
		);
	}, [items, shortcutItems, appLabel, onClickCustomizeSidebarMenuItem]);

	return (
		<GlobalMoreMenu
			appItemsData={appItemsData}
			customizeSidebarMenuItem={customizeSidebarMenuItem}
			intlMode={intlMode}
			menuItemComponent={menuItemComponent}
			onClickMoreMenuTrigger={onClickMoreMenuTrigger}
			peekingMenuIds={peekMenuId ? [peekMenuId] : undefined}
			productItemsData={productItemsData}
			testId={MORE_MENU_TEST_ID}
		/>
	);
}
